import { Command as CommandPrimitive } from "cmdk";
import {
  useCallback,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import React from "react";
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@volca/ui/components/ui/command";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@volca/ui/components/ui/input-group";
import { Spinner } from "@volca/ui/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import {
  AUTOCOMPLETE_INPUT_CLASS,
  AUTOCOMPLETE_POPUP_CLASS,
  AutoCompleteField,
  requiredPlaceholder,
  useCmdkInput,
} from "./autocomplete-field";
import { focusNextTabbable } from "./focus-next-tabbable";

type Props<T extends string> = {
  id?: string;
  selectedValue: T;
  onSelectedValueChange: (value: T) => void;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  items: { value: T; label: string }[];
  renderOption?: (option: { value: T; label: string }) => React.ReactNode;
  isLoading?: boolean;
  emptyMessage?: string;
  placeholder?: string;
  label?: string;
  fullWidth?: boolean;
  required?: boolean;
  disableRequirementAsterisk?: boolean;
  className?: string;
  scrollAreaClassName?: string;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode | string;
  selectedLabel?: string;
};

export const BackendAutoComplete = React.forwardRef<
  HTMLInputElement,
  Props<string>
>(
  (
    {
      id,
      selectedValue,
      onSelectedValueChange,
      searchValue,
      onSearchValueChange,
      items,
      renderOption,
      isLoading,
      emptyMessage = "No items.",
      placeholder = "Search…",
      label,
      fullWidth,
      required,
      disableRequirementAsterisk,
      className,
      scrollAreaClassName,
      disabled,
      disabledTooltip,
      selectedLabel,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setOpen] = useState(false);
    const [highlightedValue, setHighlightedValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const labelId = useId();
    const inputId = useCmdkInput(inputRef, label ? labelId : undefined);
    const generatedId = useId();
    const disabledInputId = id ?? generatedId;

    React.useImperativeHandle(ref, () => inputRef.current!, []);

    const displayValue =
      !isFocused && selectedValue && selectedLabel
        ? selectedLabel
        : searchValue;

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (!input) return;

        if (event.key === "Tab") return;

        if (!isOpen) {
          setOpen(true);
        }

        if (event.key === "Escape") {
          input.blur();
        }
      },
      [isOpen],
    );

    const handleInputKeyDownCapture = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Tab") {
          event.preventDefault();
          event.stopPropagation();
          focusNextTabbable(event.currentTarget, event.shiftKey);
        }
      },
      [],
    );

    const handleFocus = useCallback(() => {
      if (disabled) return;
      setOpen(true);
      setIsFocused(true);
    }, [disabled]);

    const handleInputClick = useCallback(() => {
      if (disabled || isFocused) return;
      setOpen(true);
      setIsFocused(true);
    }, [disabled, isFocused]);

    const handleBlur = useCallback(() => {
      setOpen(false);
      setIsFocused(false);
    }, []);

    const handleSelectOption = useCallback(
      (value: string) => {
        const selectedItem = items.find((item) => item.value === value);
        onSelectedValueChange(value);
        if (selectedItem) {
          onSearchValueChange(selectedItem.label);
        }
        setOpen(false);
        setIsFocused(false);
      },
      [items, onSearchValueChange, onSelectedValueChange],
    );

    const fieldProps = {
      label,
      required,
      disableRequirementAsterisk,
      disabled,
      fullWidth,
    };
    const placeholderText = requiredPlaceholder(placeholder, fieldProps);

    if (disabled) {
      const disabledField = (
        <AutoCompleteField {...fieldProps} htmlFor={disabledInputId}>
          <InputGroup className={className}>
            <InputGroupInput
              id={disabledInputId}
              disabled
              value={searchValue}
              readOnly
              placeholder={placeholderText}
              aria-required={required || undefined}
            />
          </InputGroup>
        </AutoCompleteField>
      );

      if (disabledTooltip) {
        return (
          <Tooltip>
            <TooltipTrigger
              render={<div className={cn({ "w-full": fullWidth })} />}
            >
              {disabledField}
            </TooltipTrigger>
            <TooltipContent>{disabledTooltip}</TooltipContent>
          </Tooltip>
        );
      }

      return disabledField;
    }

    const inputElement = (
      <AutoCompleteField {...fieldProps} htmlFor={inputId} labelId={labelId}>
        <CommandPrimitive
          onKeyDown={handleKeyDown}
          value={highlightedValue}
          onValueChange={setHighlightedValue}
          onMouseLeave={() => setHighlightedValue("")}
          shouldFilter={false}
        >
          <InputGroup className={className}>
            <CommandPrimitive.Input
              id={id}
              data-slot="input-group-control"
              ref={inputRef}
              value={displayValue}
              onValueChange={onSearchValueChange}
              onBlur={handleBlur}
              autoFocus={false}
              onFocus={handleFocus}
              onClick={handleInputClick}
              onKeyDownCapture={handleInputKeyDownCapture}
              placeholder={placeholderText}
              aria-required={required || undefined}
              className={AUTOCOMPLETE_INPUT_CLASS}
            />
            {isLoading && (
              <InputGroupAddon align="inline-end">
                <Spinner aria-label="Loading suggestions" />
              </InputGroupAddon>
            )}
          </InputGroup>
          <div className="relative">
            <div
              className={cn(AUTOCOMPLETE_POPUP_CLASS, {
                hidden: !isOpen || (isLoading && items.length === 0),
              })}
            >
              <CommandList className={cn("max-h-44", scrollAreaClassName)}>
                {items.length > 0 ? (
                  <CommandGroup className="p-0">
                    {items.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onSelect={() => handleSelectOption(option.value)}
                        data-checked={selectedValue === option.value}
                        className="data-checked:bg-selected data-checked:text-brand"
                      >
                        {renderOption ? renderOption(option) : option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : null}
              </CommandList>
              {/* Beside the listbox, which may only hold options. */}
              {!isLoading && items.length === 0 ? (
                <CommandEmpty className="py-3">{emptyMessage}</CommandEmpty>
              ) : null}
            </div>
          </div>
        </CommandPrimitive>
      </AutoCompleteField>
    );

    return inputElement;
  },
);
